import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ProofShowcase from '../components/ProofShowcase';
import TechnologyPartners from '../components/TechnologyPartners';
import {
  ArrowRight,
  Award,
  BadgeCheck,
  BookOpen,
  Building,
  Check,
  Clock,
  FileCheck,
  Lock,
  Scale,
  Shield,
  Stethoscope,
  TrendingDown,
  UploadCloud,
  Users,
} from 'lucide-react';

const iconStroke = 1.75;

const proofStats = [
  { label: 'Transparent Pricing', value: '$2/page' },
  { label: 'Volume Pricing Available', value: 'Enterprise', to: '/contact' },
  { label: 'Certificates Provided', value: 'AA Compliance' },
  { label: 'Detailed Remediation Report', value: 'Quality & Transparency' },
];

const heroQueueItems = [
  { name: 'Civic-services-guide.pdf', status: 'Building structure tree', progress: 84 },
  { name: 'Student-handbook.pdf', status: 'Checking reading order', progress: 62 },
  { name: 'Policy-disclosure.pdf', status: 'Preparing certificate', progress: 92 },
];

const workflow = [
  {
    icon: UploadCloud,
    title: 'Upload',
    text: 'Securely submit source PDFs through the dashboard.',
  },
  {
    icon: FileCheck,
    title: 'Remediate',
    text: 'AI-assisted structure, tags, alt text and reading-order processing.',
  },
  {
    icon: Award,
    title: 'Certify',
    text: 'Download the remediated PDF with a verifiable compliance certificate.',
  },
];

const enterpriseSignals = [
  {
    icon: Shield,
    title: 'Zero-touch privacy',
    description: 'No human reviewer needs to open your documents during processing.',
  },
  {
    icon: BookOpen,
    title: 'Screen-reader structure',
    description: 'Semantic tags, meaningful sequence and document metadata are treated as product outputs.',
  },
  {
    icon: BadgeCheck,
    title: 'Audit-ready evidence',
    description: 'Certificates and processing records support procurement, reporting and compliance review.',
  },
  {
    icon: Clock,
    title: 'Operational speed',
    description: 'Move large document queues through an automated workflow instead of manual remediation cycles.',
  },
];

const sectors = [
  { title: 'Government', icon: Building, proof: 'ADA Title II and public access obligations' },
  { title: 'Education', icon: BookOpen, proof: 'Course materials, student services and institutional publishing' },
  { title: 'Healthcare', icon: Stethoscope, proof: 'Patient information, forms and clinical education documents' },
  { title: 'Finance', icon: TrendingDown, proof: 'Statements, disclosures and regulated customer communications' },
  { title: 'Legal', icon: Scale, proof: 'Court documents, contracts and equal access to justice' },
  { title: 'Corporate', icon: Users, proof: 'Training, HR, internal policy and customer-facing documents' },
];

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>PDF AA Compliance | AI-Powered PDF Accessibility Remediation</title>
        <meta
          name="description"
          content="Remediate PDFs for WCAG 2.1 AA, PDF/UA-1 and Section 508 with certificate evidence. Simple $2/page pricing."
        />
        <link rel="canonical" href="https://pdfaa.ai/" />
        <meta property="og:title" content="PDF AA Compliance | PDF Accessibility Remediation" />
        <meta
          property="og:description"
          content="Upload, remediate, verify and download accessible PDFs with certificate evidence. Simple $2/page pricing."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pdfaa.ai/" />
        <meta property="og:image" content="https://pdfaa.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PDF AA Compliance | PDF Accessibility Remediation" />
        <meta
          name="twitter:description"
          content="AI-powered PDF accessibility remediation with verifiable certificate evidence."
        />
        <meta name="twitter:image" content="https://pdfaa.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'PDF AA Compliance',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: 'https://pdfaa.ai',
            description:
              'AI-powered PDF accessibility remediation for WCAG 2.1 AA, PDF/UA-1 and Section 508.',
            offers: {
              '@type': 'Offer',
              price: '2.00',
              priceCurrency: 'USD',
              description: 'Per-page PDF accessibility remediation with certificate evidence.',
            },
            publisher: {
              '@type': 'Organization',
              name: 'PDF AA Compliance',
              url: 'https://pdfaa.ai',
            },
          })}
        </script>
      </Helmet>

      <div className="bg-slate-900 text-white">
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#070a12_0%,#111728_50%,#142032_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-slate-950/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-20 lg:pb-20 lg:pt-24">
          <div className="grid lg:grid-cols-[1fr_0.98fr] gap-10 xl:gap-12 items-center">
            <div className="flex min-h-[440px] flex-col justify-center">
              <div className="inline-flex w-full max-w-xl items-center gap-2 rounded-full border border-primary-400/35 bg-primary-500/10 px-3 py-1.5 text-sm font-semibold text-primary-100">
                <Lock className="h-4 w-4 text-primary-400" strokeWidth={iconStroke} />
                Enterprise PDF accessibility remediation
              </div>
              <h1 className="mt-7 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-normal text-white sm:text-5xl lg:text-[3rem]">
                Make every PDF accessible, auditable and ready for review.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-200">
                pdfaa.ai turns PDF accessibility remediation into a secure workflow: upload, remediate,
                verify and download a standards-aligned document with certificate evidence.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-6 py-3 text-base font-semibold text-slate-950 shadow-sm shadow-black/20 transition-colors hover:bg-slate-100"
                >
                  Get started
                  <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
                </Link>
                <Link
                  to="/security"
                  className="inline-flex items-center justify-center rounded-md border border-slate-600 bg-slate-800 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  Review security model
                </Link>
              </div>
            </div>

            <div className="flex min-h-[440px] flex-col rounded-lg border border-slate-700 bg-slate-950/80 p-4 shadow-2xl shadow-black/50">
              <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Compliance workbench</p>
                  <h2 className="mt-1 text-lg font-semibold text-white">PDF remediation queue</h2>
                </div>
                <span className="rounded-full bg-primary-500/10 px-3 py-1 text-xs font-semibold text-primary-200">
                  Live dashboard
                </span>
              </div>

              <div className="mt-4 flex flex-1 flex-col justify-between gap-3">
                {heroQueueItems.map((item) => (
                  <div key={item.name} className="rounded-md border border-slate-700 bg-slate-800/70 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-white">{item.name}</p>
                        <p className="mt-1 text-sm text-slate-300">{item.status}</p>
                      </div>
                      <span className="whitespace-nowrap text-xs text-slate-400">Processing</span>
                    </div>

                    <div
                      className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-700"
                      role="progressbar"
                      aria-label={`${item.name} processing progress`}
                      aria-valuenow={item.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="h-full rounded-full bg-primary-400"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-lg bg-slate-950 px-5 py-6 shadow-2xl shadow-black/35 sm:px-6 lg:px-8">
            <h2 className="text-xl font-semibold text-white sm:text-2xl">Our Standards</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {proofStats.map((stat) => {
                const content = (
                  <>
                    <Check className="h-6 w-6 shrink-0 text-primary-400" strokeWidth={iconStroke} />
                    <div className="min-w-0">
                      <p className="text-xl font-semibold leading-7 text-white">{stat.value}</p>
                      <p className="mt-1 text-sm text-slate-400">{stat.label}</p>
                    </div>
                  </>
                );

                return stat.to ? (
                  <Link
                    key={stat.label}
                    to={stat.to}
                    className="flex min-h-[104px] items-center gap-4 rounded-md border border-slate-700 bg-slate-900 px-5 py-4 transition-colors hover:border-primary-500/60 hover:bg-slate-800"
                  >
                    {content}
                  </Link>
                ) : (
                  <div
                    key={stat.label}
                    className="flex min-h-[104px] items-center gap-4 rounded-md border border-slate-700 bg-slate-900 px-5 py-4"
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">Workflow</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Remediate documents at scale.</h2>
            <p className="mt-4 text-slate-300">
              The Enterprise design skill calls for structured, repeatable patterns. For pdfaa.ai that means the workflow,
              proof and operational controls stay visible from the first screen.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {workflow.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-slate-700 bg-slate-900 p-6">
                <div className="flex items-center justify-between">
                  <item.icon className="h-5 w-5 text-primary-300" strokeWidth={iconStroke} />
                  <span className="text-sm text-slate-500">0{index + 1}</span>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProofShowcase
        eyebrow="Proof from hard PDFs"
        title="Real source pages, real certificates, visible complexity."
        description="Before a team trusts automation, they need to see it handle the awkward documents: catalogues, maps and long reports with tables. These are the proof cases behind the workflow."
      />

      <TechnologyPartners band compact />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">Trust signals</p>
              <h2 className="mt-3 text-3xl font-semibold text-white">Built for teams that need evidence, not decoration.</h2>
              <p className="mt-4 text-slate-300">
                Compliance managers, accessibility leads and procurement teams need to see what the product does,
                how documents are handled and what evidence comes out the other side.
              </p>
              <Link
                to="/standards"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200"
              >
                View standards we work to
                <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
              </Link>
              <Link
                to="/case-studies"
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200"
              >
                See remediation case studies
                <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {enterpriseSignals.map((item) => (
                <div key={item.title} className="rounded-lg border border-slate-700 bg-slate-800/60 p-6">
                  <item.icon className="h-5 w-5 text-slate-200" strokeWidth={iconStroke} />
                  <h3 className="mt-4 text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">Buyer fit</p>
              <h2 className="mt-3 text-3xl font-semibold text-white">For institutions with a real duty of care.</h2>
            </div>
            <Link
              to="/savings"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200"
            >
              See pricing and savings
              <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sectors.map((item) => (
              <div key={item.title} className="rounded-lg border border-slate-700 bg-slate-900 p-5">
                <div className="flex items-center gap-3">
                  <item.icon className="h-5 w-5 text-primary-300" strokeWidth={iconStroke} />
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.proof}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center rounded-lg border border-slate-700 bg-slate-800 p-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">Compliance output</p>
              <h2 className="mt-3 text-3xl font-semibold text-white">Documents, certificates and audit evidence in one workflow.</h2>
              <p className="mt-4 text-slate-300">
                Start with a paid page pack or an approved enterprise pilot when the process is validated by your team.
              </p>
            </div>
            <div className="space-y-3">
              {[
                'WCAG 2.1 Level AA alignment',
                'PDF/UA-1 structure and metadata',
                'Section 508 and ADA-focused reporting',
                'Secure processing with no human document access',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-md bg-slate-900 px-4 py-3">
                  <Check className="h-5 w-5 text-primary-400" strokeWidth={iconStroke} />
                  <span className="text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}

